import {QuotationService, QuotationServiceActionType} from '../../../../services/quotation.service';
import {QuotationListInfoModel} from '../../../../models/quotation-list-info.model';
import {Subject} from 'rxjs';
import {MessageManagerService} from '@shared/service/message-manager/message-manager.service';
import {AuManageDealerComponent} from '../../../au-manage-dealer/au-manage-dealer.component';
import {QuotationEntry} from '../working-manager.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {WorkingModel} from '../../../../models/working.model';
import {LoaderService} from '@shared/service/loader.service';
import {ConsipApproveRequestBody, ConsipFlowService} from '../../../../services/consip-flow.service';
import {AuthorityConsipApproveDialogComponent} from '../../../../../authority-ald/components/authority-consip-approve-dialog/authority-consip-approve-dialog.component';
import {AuthorityConsipPreviewQuotationPdfDialogComponent} from '../../../../../authority-ald/components/authority-consip-preview-quotation-pdf-dialog/authority-consip-preview-quotation-pdf-dialog.component';
import {CommonActions} from './common-actions';
import { DocumentsService } from 'app/authority/authority-shared/services/documents.service';
import { DossierModel } from 'app/authority/authority-shared/models/dossier.model';

/**
 * Abstract class that contains the action that the authority user can do
 */

export abstract class AuthorityActions extends CommonActions {
	static approve(
		quotationService: QuotationService, loaderService: LoaderService, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		needRefreshDossierSubject: Subject<void>, messageManagerService: MessageManagerService
	): void {
		this.doQuotationPatch(
			quotationService, loaderService, dossierId, workingId, allQuotations, needRefreshDossierSubject, messageManagerService, QuotationServiceActionType.APPROVE
		);
	}

	static reApproveQuotation(
		quotationService: QuotationService, loaderService: LoaderService, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		needRefreshDossierSubject: Subject<void>, messageManagerService: MessageManagerService
	): void {
		this.doQuotationPatch(
			quotationService, loaderService, dossierId, workingId, allQuotations, needRefreshDossierSubject, messageManagerService, QuotationServiceActionType.RE_APPROVE
		);
	}


	static dealerChecked(
		dialog: MatDialog, selectedEntries: QuotationEntry[], dossierId: string, working: WorkingModel,
		allQuotations: QuotationListInfoModel, dossierFlowType: string, assignManyParts: boolean
	): void {
		dialog.open(AuManageDealerComponent, {
			height: 'auto',
			width: 'auto',
			backdropClass: 'amd-dialog__backdrop--dark',
			panelClass: 'amd-full-screen-simple-table-dialog',
			data: {
				entries: selectedEntries,
				dossierId: dossierId,
				workingId: working.id,
				quotationId: allQuotations.quotations[0].id,
				dialog: dialog,
				assignManyParts: assignManyParts,
				workingStatus: working.status,
				dossierFlowType: dossierFlowType
			}
		});
	}

	static getExtraTotal(
		loaderService: LoaderService, consipFlowService: ConsipFlowService, messageManagerService: MessageManagerService,
		needRefreshDossierSubject: Subject<void>, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		tableSelectedEntry: QuotationEntry[]
	): void {
		const entryIds: string[] = [];
		tableSelectedEntry.forEach(entry => entryIds.push(entry.id));
		loaderService.display(true);
		AuthorityActions.extraTotalService(loaderService, consipFlowService, messageManagerService,
			needRefreshDossierSubject, dossierId, workingId, allQuotations, entryIds, false);
	}

	static consipRemoveExtraTotal(
		loaderService: LoaderService, consipFlowService: ConsipFlowService, messageManagerService: MessageManagerService,
		needRefreshDossierSubject: Subject<void>, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		tableSelectedEntry: QuotationEntry[], tableEntry: QuotationEntry[]
	): void {
		const entryIds: string[] = [];
		let entries: QuotationEntry[] = tableEntry.filter(el => el.isForConsip);

		tableSelectedEntry.forEach(entry => {
			entries = entries.filter(el => entry.id !== el.id);
		});

		entries.forEach(entry => entryIds.push(entry.id));

		loaderService.display(true);
		AuthorityActions.extraTotalService(loaderService, consipFlowService, messageManagerService,
			needRefreshDossierSubject, dossierId, workingId, allQuotations, entryIds, true);
	}

	private static extraTotalService(
		loaderService: LoaderService, consipFlowService: ConsipFlowService, messageManagerService: MessageManagerService,
		needRefreshDossierSubject: Subject<void>, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		entryIds: string[], isRemoving: boolean
	): void {
		loaderService.display(true);
		consipFlowService.getConsipExtraTotal(dossierId, workingId, allQuotations.quotations[0].id, entryIds, isRemoving,
			message => {
				messageManagerService.pushInnerMessage(message);
				needRefreshDossierSubject.next();
			}, message => {
				messageManagerService.pushInnerMessage(message);
				loaderService.display(false);
			}
		);
	}

	static sendToConsip(
		loaderService: LoaderService, consipFlowService: ConsipFlowService, messageManagerService: MessageManagerService,
		needRefreshDossierSubject: Subject<void>, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		tableEntry: QuotationEntry[]
	): void {
		const entryIds: string[] = [];
		tableEntry.filter(el => el.isForConsip).forEach(entry => entryIds.push(entry.id));
		loaderService.display(true);
		consipFlowService.sendToConsip(dossierId, workingId, allQuotations.quotations[0].id, entryIds,
			message => {
				messageManagerService.pushInnerMessage(message);
				needRefreshDossierSubject.next();
			}, message => {
				messageManagerService.pushInnerMessage(message);
				loaderService.display(false);
			}
		);
	}

	static consipApprove(
		dialog: MatDialog, loaderService: LoaderService, consipFlowService: ConsipFlowService, messageManagerService: MessageManagerService, tableEntry: QuotationEntry[],
		needRefreshDossierSubject: Subject<void>, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel
	): void {
		const dialogRef: MatDialogRef<AuthorityConsipApproveDialogComponent> = dialog.open(AuthorityConsipApproveDialogComponent, {
			height: 'auto',
			width: '500px',
			backdropClass: 'amd-dialog__backdrop--dark',
			disableClose: true,
			data: {
				required: tableEntry.some(entry => entry.isForConsip)
			}
		});

		dialogRef.afterClosed().subscribe((requestBody: ConsipApproveRequestBody) => {
				if (!!requestBody) {
					loaderService.display(true);
					consipFlowService.consipApprove(dossierId, workingId, allQuotations.quotations[0].id,
						message => {
							messageManagerService.pushInnerMessage(message);
							needRefreshDossierSubject.next();
						}, message => {
							messageManagerService.pushInnerMessage(message);
							loaderService.display(false);
						}, requestBody);
				}
			}
		);
	}

	static sendToSupervisor(
		quotationService: QuotationService, loaderService: LoaderService, needRefreshDossierSubject: Subject<void>, messageManagerService: MessageManagerService,
		dossierId: string, workingId: string, allQuotations: QuotationListInfoModel
	): void {
		this.doQuotationPatch(
			quotationService, loaderService, dossierId, workingId, allQuotations, needRefreshDossierSubject, messageManagerService, QuotationServiceActionType.SEND_SUPERVISOR
		);
	}

	static reject(
		quotationService: QuotationService, loaderService: LoaderService, dossierId: string, workingId: string, allQuotations: QuotationListInfoModel,
		needRefreshDossierSubject: Subject<void>, messageManagerService: MessageManagerService
	): void {
		this.doQuotationPatch(
			quotationService, loaderService, dossierId, workingId, allQuotations, needRefreshDossierSubject, messageManagerService, QuotationServiceActionType.REJECT
		);
	}
	
	static previewQuotationPdf(
		dialog: MatDialog, dossier_id: string, working_id: string, quotation_id: string, documentService: DocumentsService, loaderService: LoaderService
	): void {

		loaderService.display(true);
		documentService.previewQuotationPdf(dossier_id, working_id, quotation_id).subscribe((resp) => {
			loaderService.display(false);
			
			const dialogRef: MatDialogRef<AuthorityConsipPreviewQuotationPdfDialogComponent> = dialog.open(AuthorityConsipPreviewQuotationPdfDialogComponent, {
				height: 'auto',
				width: 'auto',
				backdropClass: 'amd-dialog__backdrop--dark',
				panelClass: 'amd-full-screen-simple-table-dialog',
				data: {
					consip: resp
				}
			});

		});
	}

	/**
	 * Esegue l'azione di forzare il refresh della quotazione per una specifica working.
	 * Mostra il loader durante l'operazione, chiama il servizio di refresh e gestisce la risposta.
	 * In caso di successo, notifica i subscriber per aggiornare il dossier e mostra un messaggio di successo.
	 * In caso di errore, mostra un messaggio di errore.
	 *
	 * @param dossier Il modello del dossier corrente
	 * @param workingId L'ID della working su cui forzare il refresh
	 * @param quotationId L'ID della quotazione da aggiornare
	 * @param needRefreshDossierSubject Subject per notificare il refresh del dossier
	 * @param loaderService Service per mostrare/nascondere il loader
	 * @param quotationService Service per le operazioni sulle quotazioni
	 * @param messageManagerService Service per la gestione dei messaggi utente
	 */
	static forceQuotationRefresh(
		dossier: DossierModel,
		workingId: string,
		quotationId: string,
		needRefreshDossierSubject: Subject<void>,
		loaderService: LoaderService,
		quotationService: QuotationService,
		messageManagerService: MessageManagerService): void {
		loaderService.display(true);
		quotationService.forceQuotationRefresh(
			dossier,
			workingId,
			quotationId,
			successMessage => {
				loaderService.display(false);
				needRefreshDossierSubject.next();
				messageManagerService.pushInnerMessage(successMessage);
			}, errorMessage => {
				loaderService.display(false);
				messageManagerService.pushInnerMessage(errorMessage);
			}
		
		);
	}





}
